import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { Language, en, loadTranslation, getCachedTranslation, type TranslationKeys } from "@/i18n";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
  availableLanguages: { code: Language; name: string; nativeName: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "taskratel-language";

const availableLanguages: { code: Language; name: string; nativeName: string }[] = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "yo", name: "Yoruba", nativeName: "Yorùbá" },
  { code: "ha", name: "Hausa", nativeName: "Hausa" },
  { code: "ig", name: "Igbo", nativeName: "Igbo" },
];

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && (stored === "en" || stored === "yo" || stored === "ha" || stored === "ig")) {
        return stored as Language;
      }
    }
    return "en";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    // Update HTML lang attribute
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const [t, setT] = useState<TranslationKeys>(() => getCachedTranslation(language));

  useEffect(() => {
    let active = true;
    loadTranslation(language)
      .then((tr) => { if (active) setT(tr); })
      .catch(() => { if (active) setT(en); });
    return () => { active = false; };
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, availableLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // Graceful fallback (e.g. during HMR re-mounts) instead of crashing the app
    return {
      language: "en" as Language,
      setLanguage: () => {},
      t: en,
      availableLanguages,
    };
  }
  return context;
}
